import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wallet, TrendingDown, TrendingUp } from "lucide-react";
import type { LunimarRealm } from "@/components/lunimar/lunimarRealms";
import { fmtMoney } from "./types";

interface Props {
  realm?: LunimarRealm;
  /** Net wallet change (positive = credit, negative = debit). */
  delta: number;
  /** Optional running balance after delta for context. */
  balanceAfter?: number;
  /** Short label like "Simulated impact" or "Monthly cashflow". */
  label?: string;
  /** Persisted = true when the value writes to the real wallet. */
  persisted?: boolean;
}

/**
 * Animated wallet-impact display. Counts up/down to the delta value.
 * Use after a consequence to show how the simulation moved real money.
 */
const SimWalletImpact = ({
  realm,
  delta,
  balanceAfter,
  label = "Wallet impact",
  persisted = true,
}: Props) => {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? delta : 0);

  useEffect(() => {
    if (reduce) {
      setShown(delta);
      return;
    }
    const start = performance.now();
    const dur = 700;
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(delta * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [delta, reduce]);

  const positive = delta >= 0;
  const tone = positive ? "152 70% 60%" : "0 78% 60%";
  const accent = realm?.primaryAccent ?? tone;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative overflow-hidden rounded-xl border p-3 backdrop-blur-md sm:p-4"
      style={{
        borderColor: `hsl(${tone} / 0.4)`,
        background: `linear-gradient(135deg, hsl(${tone} / 0.12), hsl(228 45% 10% / 0.6))`,
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/60">
          <Wallet className="h-3 w-3" />
          {label}
          {!persisted && (
            <span className="rounded-sm border border-white/20 bg-white/10 px-1 py-px text-[9px] text-white/70">
              SIM
            </span>
          )}
        </div>
        {positive ? (
          <TrendingUp className="h-4 w-4" style={{ color: `hsl(${tone})` }} />
        ) : (
          <TrendingDown className="h-4 w-4" style={{ color: `hsl(${tone})` }} />
        )}
      </div>
      <div
        className="mt-1 font-mono text-2xl font-bold tabular-nums sm:text-3xl"
        style={{ color: `hsl(${tone})` }}
      >
        {fmtMoney(shown)}
      </div>
      {typeof balanceAfter === "number" && (
        <div className="mt-1 text-[11px] text-white/60">
          New balance: <span className="text-white/85 font-mono">{fmtMoney(balanceAfter).replace("+", "")}</span>
        </div>
      )}
      {accent && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-3 bottom-0 h-px"
          style={{
            background: `linear-gradient(90deg, transparent, hsl(${tone} / 0.6), transparent)`,
          }}
        />
      )}
    </motion.div>
  );
};

export default SimWalletImpact;